import { useEffect } from 'react';
import { useApiQuery } from './useApiQuery';
import { useAuth } from '@clerk/react';
import apiService from '../services/api';

export type OnboardingState = 'plan_selection' | 'awaiting_payment' | 'campaign_creation' | 'complete';

export interface CurrentUserPlan {
  id: number;
  name: string;
  price: number;
}

export type PlanFeatureKey =
  | 'create_campaign'
  | 'generate_content'
  | 'view_prospects'
  | 'run_campaign'
  | 'send_outreach'
  | 'deploy_ads'
  | 'view_full_prospect_contact'
  | 'view_full_ad_copy'
  | 'view_performance_metrics';

interface CurrentUser {
  localUserId: number;
  email: string | null;
  displayName: string | null;
  role: string;
  allowedCampaigns: string[] | null;
  allowedPages: string[] | null;
  tenantSlug: string | null;
  tenantId?: string | null;
  onboardingState?: OnboardingState;
  plan?: CurrentUserPlan | null;
  featureFlags?: Partial<Record<PlanFeatureKey, boolean>>;
}

interface MeResponse {
  success: boolean;
  user: CurrentUser;
}

const PERMISSIONS_POLL_INTERVAL = 60_000; // 60s

export function useCurrentUser() {
  const { isSignedIn } = useAuth();

  const query = useApiQuery<MeResponse>(
    () => apiService.get<MeResponse>('/me'),
    { enabled: !!isSignedIn, deps: [isSignedIn] }
  );

  // Silently poll /api/me to pick up role/permission changes without loading flashes
  useEffect(() => {
    if (!isSignedIn) return;

    const interval = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      try {
        const result = await apiService.get<MeResponse>('/me');
        query.setData(result);
      } catch {
        // Silently ignore polling errors — next interval will retry
      }
    }, PERMISSIONS_POLL_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  return { ...query, refetchUser: query.refetch };
}
